import React, { useRef, useEffect } from 'react'
import {
  AppDynamicChatMessage,
  UIComponentProps,
} from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppDynamicChatMessage.component'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { SafeMarkdown } from 'components/Common/SafeMarkdown/SafeMarkdown.component'
import {
  OnboardingFlowStepTypes,
  OnboardingFlowStepStatus,
  GoalHistorySection as GoalHistorySectionType,
} from '__generated__/api-types-and-hooks'
import Avatar from 'appAssets/appImages/Avatar.png'
import moment from 'moment-timezone'

type Props = {
  section: GoalHistorySectionType
  messages: any[]
  hasMore: boolean
  isLoading: boolean
  sessionId: string
  userInitial: string
  user: {
    firstName?: string | null
    lastName?: string | null
    name?: string | null
  } | null
  onLoadMore: (sessionId: string) => void
  scrollContainerRef: (el: HTMLDivElement | null) => void
  sentinelRef: (el: HTMLDivElement | null) => void
}

const convertMessage = (message: {
  text: string
  senderRole: string
  senderName?: string | null
  avatar?: string | null
  createdAt: string
}): {
  sender: string
  text?: string
  content?: UIComponentProps
  createdAt: string
} => {
  if (message?.text?.startsWith('{')) {
    try {
      const content = JSON.parse(message.text)
      return {
        sender: message.senderRole === 'assistant' ? 'assistant' : 'user',
        content,
        createdAt: message.createdAt,
      }
    } catch (e) {
      // If JSON parse fails, treat as plain text
    }
  }
  return {
    sender: message.senderRole === 'assistant' ? 'assistant' : 'user',
    text: message.text ?? '',
    createdAt: message.createdAt,
  }
}

export const GoalHistorySection: React.FC<Props> = React.memo(
  ({
    section,
    messages,
    hasMore,
    isLoading,
    sessionId,
    userInitial,
    user,
    onLoadMore,
    scrollContainerRef,
    sentinelRef,
  }) => {
    const containerRef = useRef<HTMLDivElement | null>(null)

    // Update the ref callback
    useEffect(() => {
      if (containerRef.current) {
        scrollContainerRef(containerRef.current)
      }
      return () => {
        scrollContainerRef(null)
      }
    }, [scrollContainerRef])

    return (
      <div className="bg-white rounded-lg p-4">
        <div className="mb-4 pb-2 border-b border-app-grey-20">
          <div className="text-sm font-semibold text-app-grey-100">{section?.title}</div>
          {section?.createdAt && (
            <div className="text-xs text-app-grey-60 mt-1">
              {moment(section.createdAt).format('MMMM DD, YYYY • hh:mm A')}
            </div>
          )}
        </div>
        <div
          ref={containerRef}
          className="section-scroll-container max-h-[400px] overflow-y-auto pr-2 space-y-4"
        >
          {/* Loading indicator at TOP */}
          {isLoading && (
            <div className="flex justify-center py-4">
              <div className="text-sm text-app-grey-60">Loading older messages...</div>
            </div>
          )}

          {/* Sentinel element for intersection observer - at TOP to trigger when scrolling up */}
          {hasMore && !isLoading && (
            <div ref={sentinelRef} className="h-1 w-full" style={{ minHeight: '1px' }} />
          )}

          {messages.map((m, i) => {
            const convertedMessage = convertMessage({
              text: m?.text || '',
              senderRole: m?.senderRole || 'assistant',
              senderName: m?.senderName,
              avatar: m?.avatar,
              createdAt: m?.createdAt || '',
            })

            const isUser = convertedMessage.sender === 'user'
            const userAvatar = isUser ? m?.avatar || '' : ''
            const logo = isUser ? userAvatar : undefined

            const senderName = isUser
              ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'User'
              : m?.senderName || 'Coach Logic'

            const timestamp = moment(Number(m?.createdAt)).format('MMM DD, YYYY, hh:mm A')

            return (
              <div
                key={`${i}-${m?.createdAt}`}
                className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className={isUser ? 'order-2' : ''}>
                  <AppAvatar
                    size={'md'}
                    type={!logo && isUser ? 'text' : 'image'}
                    shape={'Rounded'}
                    text={userInitial}
                    color="Primary"
                    src={isUser ? logo : Avatar}
                  />
                </div>
                <div
                  className={`flex flex-col ${
                    isUser ? 'items-end order-1' : 'items-start'
                  } max-w-[80%]`}
                >
                  <div
                    className={`flex items-center gap-2 mb-1 text-xs ${
                      isUser ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <span className="font-semibold text-app-grey-100">{senderName}</span>
                    <span className="text-app-grey-60">{timestamp}</span>
                  </div>
                  {convertedMessage.content ? (
                    <div className="w-full">
                      <AppDynamicChatMessage
                        content={
                          convertedMessage.content.type === OnboardingFlowStepTypes.Form
                            ? {
                                ...convertedMessage.content,
                                status: OnboardingFlowStepStatus.Done,
                              }
                            : convertedMessage.content
                        }
                        onSubmit={() => {
                          // Read-only: no submission allowed
                        }}
                        typingAnimationCallback={() => {}}
                        useTypingAnimation={false}
                        isLastMessage={false}
                        isSendingMessageTobot={false}
                        tools={[]}
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-fit max-w-full flex rounded-lg px-3 pt-2 pb-1.5 text-base text-black-appDark !font-inter ${
                        convertedMessage.sender === 'assistant'
                          ? 'bg-background-appLight text-black'
                          : 'bg-primary-appBrand text-white'
                      }`}
                    >
                      {convertedMessage.sender === 'assistant' ? (
                        <SafeMarkdown className="chatbot-markdown">
                          {convertedMessage.text || ''}
                        </SafeMarkdown>
                      ) : (
                        convertedMessage.text
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if these props change
    // Note: messages array reference will change when new messages are added,
    // so we check length and first/last message IDs to detect changes
    const messagesChanged =
      prevProps.messages.length !== nextProps.messages.length ||
      (prevProps.messages.length > 0 &&
        nextProps.messages.length > 0 &&
        (prevProps.messages[0]?.createdAt !== nextProps.messages[0]?.createdAt ||
          prevProps.messages[prevProps.messages.length - 1]?.createdAt !==
            nextProps.messages[nextProps.messages.length - 1]?.createdAt))

    return (
      !messagesChanged &&
      prevProps.hasMore === nextProps.hasMore &&
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.sessionId === nextProps.sessionId
    )
  }
)

GoalHistorySection.displayName = 'GoalHistorySection'
